import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import userCourseHistoryApi from 'Api/axios/user-course-histories-api';
import useEmployeeApi from 'Hooks/useEmployeeApi';
import useTranslation from 'Hooks/useTranslation';
import { downloadFile } from 'Utils/file-utils';
import type {
  HistoryEmployeeReportFilterI,
  HistoryEmployeeCourseI,
  HistoryEmployeeReportTotalsI,
  PaginationMetaI,
} from 'types';

const defaultTotals: HistoryEmployeeReportTotalsI = {
  totalCourses: 0,
  completedCourses: 0,
  currentCourses: 0,
  pendingCourses: 0,
  expiredCourses: 0,
  totalTrainingHours: 0,
};

export const useHistoryEmployeeReportPage = (employeeId: string) => {
  const history = useHistory();
  const { t2 } = useTranslation('history_employee_report');
  const [currentFilters, setCurrentFilters] = useState<HistoryEmployeeReportFilterI>({});
  const [data, setData] = useState<HistoryEmployeeCourseI[]>([]);
  const [pagination, setPagination] = useState<PaginationMetaI | null>(null);
  const [totals, setTotals] = useState<HistoryEmployeeReportTotalsI>(defaultTotals);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { employee, fetchEmployeeById, loading: employeeLoading, error: employeeError } = useEmployeeApi();

  const fetchEmployeeCourses = async (filters: HistoryEmployeeReportFilterI = {}) => {
    setLoading(true);
    setError(null);

    const response = await userCourseHistoryApi.getEmployeeResource(employeeId, filters);

    setLoading(false);

    if (response.data) {
      setData(response.data.rows ?? []);
      setTotals(response.data.totals ?? defaultTotals);
      setPagination(response.pagination ?? null);
    } else {
      setError(response.error ?? 'No hay datos disponibles');
    }
  };

  const handleSearch = (filters: HistoryEmployeeReportFilterI) => {
    setCurrentFilters(filters);
    fetchEmployeeCourses({ ...filters, page: 1 });
  };

  const handlePageChange = (page: number) => {
    const newFilters = { ...currentFilters, page };
    setCurrentFilters(newFilters);
    fetchEmployeeCourses(newFilters);
  };

  const handleRowsPerPageChange = (limit: number) => {
    const newFilters = { ...currentFilters, limit, page: 1 };
    setCurrentFilters(newFilters);
    fetchEmployeeCourses(newFilters);
  };

  const handleExport = async () => {
    const response = await userCourseHistoryApi.exportEmployeeResource(employeeId, currentFilters);

    if (response.data) {
      downloadFile(response.data, `historial_${employee?.fullName ?? 'empleado'}.xlsx`);
    }
  };

  const handleDownloadCertificate = async (historyId: string, certificationId: string) => {
    try {
      const response = await userCourseHistoryApi.downloadCertificate(historyId, certificationId);
      if (response.data) {
        downloadFile(response.data, `certificado_${employee?.fullName}.pdf`);
        toast.success(t2('certificate_downloaded'));
      }
    } catch (err) {
      console.error('Error downloading certificate:', err);
    }
  };

  const handleRowClick = (row: { courseId: string }) => {
    history.push(`/reporte/historial/cursos/${row.courseId}`);
  };

  useEffect(() => {
    if (employeeId) {
      fetchEmployeeById(employeeId);
      fetchEmployeeCourses({ page: 1, limit: 15 });
    }
  }, [employeeId]);

  return {
    t2,
    data,
    pagination,
    totals,
    loading,
    error,
    employee,
    employeeLoading,
    employeeError,
    currentFilters,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
    handleDownloadCertificate,
    handleRowClick,
  };
};
